import React, { useState, useEffect, FormEvent } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { Pencil, Trash2, Plus, Image as ImageIcon, X, Loader2, Upload, Link as LinkIcon, MessageSquareMore, Play, ExternalLink, LogOut, Calendar, MapPin, Building2, AlertCircle, ClipboardList, Download, Mail, Phone, Briefcase } from 'lucide-react';
import PageLayout from '../components/PageLayout';
import Modal from '../components/Modal';
import ImageUpload from '../components/ImageUpload';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminAuth from '../components/AdminAuth';

interface HeroSlide {
  id: string;
  image_url: string;
  title: string;
  subtitle: string;
  order: number;
}

interface Testimonial {
  id: string;
  name: string;
  role: string;
  content: string;
  image_url: string;
}

interface Video {
  id: string;
  video_id: string;
  title: string;
  description: string;
}

interface Project {
  id: string;
  title: string;
  description: string;
  image_url: string;
  category: string;
  completion_date: string | null;
  location: string;
  featured: boolean;
  status: 'ongoing' | 'completed' | 'upcoming';
}

interface Application {
  id: string;
  created_at: string;
  name: string;
  email: string;
  phone: string;
  experience: number;
  software_expertise: string[];
  files: { name: string; url: string; type: string; size: number }[];
  status: 'received' | 'in review' | 'interviewing' | 'rejected' | 'hired';
}

const AdminHeader = () => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/admin');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="relative bg-gradient-to-r from-primary-600 to-primary-500 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-primary-500/10 to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent"></div>
        <div className="absolute inset-0 pattern-dots opacity-10"></div>
      </div>
      <div className="relative border-b border-white/10">
        <div className="container-padding py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <img 
                src="./logo.png" 
                alt="Master Home Logo" 
                className="h-8 w-auto brightness-0 invert" 
              />
              <span className="font-medium">Master Home</span>
            </Link>
            <div className="flex items-center gap-6">
              <Link
                to="/"
                className="flex items-center gap-2 text-white/90 hover:text-white transition-colors text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                View Site
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-all text-sm"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="relative container-padding py-12">
        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 text-shadow-sm">
            Admin Dashboard
          </h1>
          <p className="text-white/90 text-lg">
            Manage your website content
          </p>
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const { session, isLoading: authLoading } = useAuth();
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'slides' | 'testimonials' | 'videos' | 'projects' | 'applications'>('slides');
  const [editingItem, setEditingItem] = useState<HeroSlide | Testimonial | Video | Project | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string, type: typeof activeTab } | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<HeroSlide | Testimonial | Video | Project>>({});
  const [uploadType, setUploadType] = useState<'url' | 'upload'>('url');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session) {
      fetchData();
    }
  }, [session]);

  const fetchData = async () => {
    try {
      setDataLoading(true);
      const [slidesData, testimonialsData, videosData, projectsData, applicationsData] = await Promise.all([
        supabase.from('hero_slides').select('*').order('order'),
        supabase.from('testimonials').select('*').order('created_at', { ascending: false }),
        supabase.from('youtube_videos').select('*').order('created_at', { ascending: false }),
        supabase.from('projects').select('*').order('created_at', { ascending: false }),
        supabase.from('applications').select('*').order('created_at', { ascending: false })
      ]);

      if (slidesData.error) throw slidesData.error;
      if (testimonialsData.error) throw testimonialsData.error;
      if (videosData.error) throw videosData.error;
      if (projectsData.error) throw projectsData.error;
      if (applicationsData.error) throw applicationsData.error;

      setSlides(slidesData.data || []);
      setTestimonials(testimonialsData.data || []);
      setVideos(videosData.data || []);
      setProjects(projectsData.data || []);
      setApplications(applicationsData.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load content. Some data may be missing.');
    } finally {
      setDataLoading(false);
    }
  };

  const getTableName = (type: typeof activeTab) => {
    const tableMap = {
      slides: 'hero_slides',
      testimonials: 'testimonials',
      videos: 'youtube_videos',
      projects: 'projects',
      applications: 'applications'
    };
    return tableMap[type];
  };

  const TabButton = ({ tab, label, icon }: { tab: typeof activeTab, label: string, icon: React.ReactNode }) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all w-full md:w-auto ${
        activeTab === tab 
          ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg transform hover:scale-[1.02]'
          : 'text-gray-600 hover:bg-gray-100 hover:text-primary-600'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  const handleEdit = (item: HeroSlide | Testimonial | Video | Project) => {
    setEditingItem(item);
    setFormData(item);
  };

  const handleDeleteClick = (id: string, type: typeof activeTab) => {
    setItemToDelete({ id, type });
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      const table = getTableName(itemToDelete.type);
      const { error: deleteError } = await supabase.from(table).delete().eq('id', itemToDelete.id);
      if (deleteError) throw new Error(`Failed to delete ${itemToDelete.type}: ${deleteError.message}`);
      await fetchData();
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete item');
    }
  };

  const handleStatusUpdate = async (id: string, newStatus: Application['status']) => {
    try {
      const { error } = await supabase.from('applications').update({ status: newStatus }).eq('id', id);
      if (error) throw error;
      setApplications(apps => apps.map(app => app.id === id ? { ...app, status: newStatus } : app));
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update application status.');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const table = getTableName(activeTab);
      const requiredFields = {
        slides: ['title', 'subtitle', 'image_url'],
        testimonials: ['name', 'role', 'content', 'image_url'],
        videos: ['title', 'description', 'video_id'],
        projects: ['title', 'description', 'image_url', 'category'],
        applications: []
      };

      const missingFields = requiredFields[activeTab].filter(
        field => !formData[field as keyof typeof formData]
      );

      if (missingFields.length > 0) {
        setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      if (editingItem) {
        const { id, ...dataToUpdate } = formData;
        const { error: updateError } = await supabase.from(table).update(dataToUpdate).eq('id', editingItem.id);
        if (updateError) throw new Error(`Failed to update ${activeTab}: ${updateError.message}`);
      } else {
        const { error: insertError } = await supabase.from(table).insert([formData]).select();
        if (insertError) throw new Error(`Failed to create ${activeTab}: ${insertError.message}`);
      }

      await fetchData();
      setFormData({});
      setEditingItem(null);
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error saving item:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  const handleImageUploadComplete = (publicUrl: string) => {
    setFormData({ ...formData, image_url: publicUrl });
  };

  const isValidUrl = (urlString: string) => {
    try { new URL(urlString); return true; } catch (e) { return false; }
  };

  const extractVideoId = (url: string): string | null => {
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('youtube.com')) return urlObj.searchParams.get('v');
      if (urlObj.hostname === 'youtu.be') return urlObj.pathname.slice(1);
      return null;
    } catch (e) { return null; }
  };

  const renderForm = () => {
    // ... (existing renderForm logic remains the same)
  };

  const ErrorAlert = ({ message, onClose }: { message: string; onClose: () => void }) => (
    <div className="fixed top-4 right-4 z-50 max-w-md bg-red-50 border-l-4 border-red-500 p-4 rounded shadow-lg">
      <div className="flex items-start">
        <div className="flex-shrink-0"><AlertCircle className="h-5 w-5 text-red-500" /></div>
        <div className="ml-3 flex-1"><p className="text-sm text-red-700">{message}</p></div>
        <button onClick={onClose} className="ml-4 flex-shrink-0 inline-flex text-red-500 hover:text-red-700"><X className="h-5 w-5" /></button>
      </div>
    </div>
  );

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>;
  }

  if (!session) {
    return <AdminAuth />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-x-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary-50/30 via-transparent to-transparent"></div>
      <div className="absolute inset-0 pattern-dots opacity-5"></div>
      <AdminHeader />

      <main className="relative py-16 md:py-24">
        <div className="container-padding">
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row justify-center gap-4 p-4 bg-white rounded-2xl shadow-sm overflow-x-auto">
              <TabButton tab="slides" label="Hero Slides" icon={<ImageIcon className="w-5 h-5" />} />
              <TabButton tab="testimonials" label="Testimonials" icon={<MessageSquareMore className="w-5 h-5" />} />
              <TabButton tab="videos" label="Learning" icon={<Play className="w-5 h-5" />} />
              <TabButton tab="projects" label="Projects" icon={<Building2 className="w-5 h-5" />} />
              <TabButton tab="applications" label="Applications" icon={<ClipboardList className="w-5 h-5" />} />
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              {dataLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-primary-600 animate-spin" /></div>
              ) : (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gradient">
                      {activeTab === 'slides' && 'Hero Slides'}
                      {activeTab === 'testimonials' && 'Testimonials'}
                      {activeTab === 'videos' && 'Learning Content'}
                      {activeTab === 'projects' && 'Projects'}
                      {activeTab === 'applications' && `Applications (${applications.length})`}
                    </h2>
                    {activeTab !== 'applications' && (
                      <button onClick={() => { setFormData({}); setIsAddModalOpen(true); }} className="btn-primary flex items-center gap-2">
                        <Plus className="w-5 h-5" /> Add New
                      </button>
                    )}
                  </div>

                  {activeTab === 'applications' ? (
                    <div className="space-y-4">
                      {applications.map(app => (
                        <motion.div key={app.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-4 group relative">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2 space-y-3">
                              <div className="flex items-center gap-3">
                                <h3 className="font-bold text-lg">{app.name}</h3>
                                <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                                  {received: 'bg-blue-100 text-blue-700', 'in review': 'bg-yellow-100 text-yellow-700', interviewing: 'bg-purple-100 text-purple-700', hired: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700'}[app.status]
                                }`}>{app.status.replace(/_/g, ' ')}</span>
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-600">
                                <a href={`mailto:${app.email}`} className="flex items-center gap-1.5 hover:text-primary-600"><Mail className="w-4 h-4" /> {app.email}</a>
                                {app.phone && <a href={`tel:${app.phone}`} className="flex items-center gap-1.5 hover:text-primary-600"><Phone className="w-4 h-4" /> {app.phone}</a>}
                                <div className="flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {app.experience} years experience</div>
                              </div>
                              {app.software_expertise?.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-800 mb-1">Software Skills:</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {app.software_expertise.map(skill => <span key={skill} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-md">{skill}</span>)}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-gray-800">Files:</h4>
                              {app.files?.map(file => (
                                <a key={file.url} href={file.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary-600 hover:underline">
                                  <Download className="w-4 h-4" />
                                  <span className="truncate">{file.name}</span>
                                </a>
                              ))}
                            </div>
                          </div>
                          <div className="absolute top-2 right-2 flex gap-2 items-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <select value={app.status} onChange={(e) => handleStatusUpdate(app.id, e.target.value as Application['status'])} className="text-xs border-gray-300 rounded-md shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50">
                              <option value="received">Received</option>
                              <option value="in review">In Review</option>
                              <option value="interviewing">Interviewing</option>
                              <option value="hired">Hired</option>
                              <option value="rejected">Rejected</option>
                            </select>
                            <button onClick={() => handleDeleteClick(app.id, 'applications')} className="p-2 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors">
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {/* ... existing rendering logic for slides, testimonials, etc. */}
                    </div>
                  )}
                </div>
              )}
            </div>

            <Modal isOpen={isAddModalOpen || !!editingItem} onClose={() => { setIsAddModalOpen(false); setEditingItem(null); setFormData({}); }}>
              <form onSubmit={handleSubmit} className="p-6">
                <h3 className="text-xl font-bold mb-6">{editingItem ? 'Edit' : 'Add New'} {activeTab.slice(0, -1)}</h3>
                {renderForm()}
                <div className="flex justify-end gap-4 mt-6">
                  <button type="button" onClick={() => { setIsAddModalOpen(false); setEditingItem(null); setFormData({}); }} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                  <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700">{editingItem ? 'Save Changes' : 'Create'}</button>
                </div>
              </form>
            </Modal>

            <Modal isOpen={isDeleteModalOpen} onClose={() => { setIsDeleteModalOpen(false); setItemToDelete(null); }}>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-4">Confirm Delete</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete this item? This action cannot be undone.</p>
                <div className="flex justify-end gap-4">
                  <button onClick={() => { setIsDeleteModalOpen(false); setItemToDelete(null); }} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                  <button onClick={handleDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700">Delete</button>
                </div>
              </div>
            </Modal>

            {error && <ErrorAlert message={error} onClose={() => setError(null)} />}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Admin;
